/**
 * The latency and loss tool (BUILD_PLAN Phase 4: "a dev tool that injects 80–200 ms latency
 * and packet loss"). Messages in each direction are delayed by half the round trip plus jitter,
 * in order. Loss is modelled the way a lost TCP segment shows up on a WebSocket: the message
 * and everything behind it stall for one extra round trip, because the stream is reliable and
 * ordered and a retransmission is what a dropped frame costs.
 */
export interface NetConditions {
  /** Round-trip latency to add, in milliseconds (half each way). */
  lagMs: number;
  /** Uniform random extra delay per message, in milliseconds. */
  jitterMs: number;
  /** Probability per message of a retransmission stall. */
  loss: number;
}

export const NO_CONDITIONS: NetConditions = { lagMs: 0, jitterMs: 0, loss: 0 };

export function isConditioned(c: NetConditions): boolean {
  return c.lagMs > 0 || c.jitterMs > 0 || c.loss > 0;
}

interface Lane {
  lastDeliverAt: number;
  /** Messages waiting for their timer, oldest first; every timer delivers the head. */
  queue: (() => void)[];
}

export class Conditioner {
  private conditions: NetConditions = NO_CONDITIONS;
  private readonly inbound: Lane = { lastDeliverAt: 0, queue: [] };
  private readonly outbound: Lane = { lastDeliverAt: 0, queue: [] };
  private readonly timers = new Set<ReturnType<typeof setTimeout>>();
  private disposed = false;

  constructor(
    conditions: NetConditions = NO_CONDITIONS,
    private readonly random: () => number = Math.random,
    private readonly now: () => number = () => performance.now(),
  ) {
    this.set(conditions);
  }

  get current(): NetConditions {
    return this.conditions;
  }

  set(conditions: NetConditions): void {
    this.conditions = {
      lagMs: Math.max(0, conditions.lagMs),
      jitterMs: Math.max(0, conditions.jitterMs),
      loss: Math.min(1, Math.max(0, conditions.loss)),
    };
  }

  /** Runs `deliver` after the inbound delay (or at once when unconditioned). */
  receive(deliver: () => void): void {
    this.schedule(this.inbound, deliver);
  }

  /** Runs `send` after the outbound delay (or at once when unconditioned). */
  send(send: () => void): void {
    this.schedule(this.outbound, send);
  }

  /** Delay the next message in a lane would get right now, in milliseconds. */
  delayFor(lane: 'in' | 'out'): number {
    const c = this.conditions;
    if (!isConditioned(c)) return 0;
    let delay = c.lagMs / 2 + this.random() * c.jitterMs;
    if (c.loss > 0 && this.random() < c.loss) delay += c.lagMs;
    const l = lane === 'in' ? this.inbound : this.outbound;
    const at = Math.max(this.now() + delay, l.lastDeliverAt);
    return at - this.now();
  }

  dispose(): void {
    this.disposed = true;
    for (const t of this.timers) clearTimeout(t);
    this.timers.clear();
  }

  private schedule(lane: Lane, fn: () => void): void {
    if (this.disposed) return;
    if (!isConditioned(this.conditions) && lane.queue.length === 0) {
      lane.lastDeliverAt = this.now();
      fn();
      return;
    }
    const delay = this.delayFor(lane === this.inbound ? 'in' : 'out');
    const at = this.now() + delay;
    lane.lastDeliverAt = at;
    // The timer delivers the oldest waiting message, not its own: browsers round timeouts to
    // whole milliseconds, so two timers due at the same instant may fire in either order, and
    // the stream must stay ordered like the socket it stands in for.
    lane.queue.push(fn);
    const timer = setTimeout(() => {
      this.timers.delete(timer);
      const next = lane.queue.shift();
      if (!this.disposed && next) next();
    }, delay);
    this.timers.add(timer);
  }
}
